import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Autocomplete,
} from '@mui/material';
import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StepperActions from './components/StepperActions';
import {
    AddSelectionCriterionRequest,
    SelectionCriteriaCatalogItem,
    SelectionCriterionResult,
    TenderSelectionCriterion,
} from './dto/tender.dto';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import {
    addSelectionCriterion,
    deleteSelectionCriterion,
    fetchSelectionCriteriaCatalog,
    updateSelectionCriterionResults,
} from './offer-evaluation.slice';
import { useTranslation } from 'react-i18next';
import { isProblemDetail } from 'src/common/ProblemDetail';

interface CriteriaWithValue extends TenderSelectionCriterion {
    customValue?: string; // For placeholders like "X Mio. EUR"
    hasError?: boolean; // Validation error flag
}

/**
 * Message to show the user when a delete fails. Prefers the backend's
 * problem-detail text, then a plain Error's own message (a network failure
 * still rejects with the AxiosError, whose message is "Network Error"),
 * before falling back to the generic string.
 */
function deleteErrorText(error: unknown): string {
    if (typeof error === 'string') return error;
    if (isProblemDetail(error)) return error.detail;
    if (error instanceof Error) return error.message;
    return 'Failed to delete selection criterion';
}

export default function OfferEvaluationStepSelectionCriteria() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        tenderSelectionCriteria,
        tenderSelectionCriteriaLoading,
        tenderSelectionCriteriaError,
        selectionCriteriaCatalog,
        selectionCriteriaLoading,
        offerSubmissions,
    } = useAppSelector((state) => state.offerEvaluation);

    const [criteria, setCriteria] = useState<CriteriaWithValue[]>([]);
    const [isAddingFromCatalog, setIsAddingFromCatalog] = useState<boolean>(false);
    const [addCriterionLoading, setAddCriterionLoading] = useState<boolean>(false);
    const [deletingIds, setDeletingIds] = useState<string[]>([]);
    const [apiError, setApiError] = useState<string | null>(null);

    // Load criteria catalog on mount (tender criteria come from tender object in Redux)
    useEffect(() => {
        dispatch(fetchSelectionCriteriaCatalog());
    }, [dispatch]);

    // Initialize and sync criteria from tender selection criteria.
    //
    // Syncs unconditionally: skipping the empty case kept the previous tender's
    // rows on screen when switching to a tender that has no criteria (this page
    // component is reused across tender ids, so its state survives the
    // navigation). Deleting one of those leftover rows sent a DELETE for a
    // criterion the current tender never had, which 404s (ADMI-FRONTEND-5Z).
    useEffect(() => {
        // Convert tender criteria to CriteriaWithValue
        const criteriaWithValues = tenderSelectionCriteria.map((item: TenderSelectionCriterion) => ({ ...item }));
        setCriteria(criteriaWithValues);
    }, [tenderSelectionCriteria]);

    const handleDeleteCriteria = async (criterionId: string) => {
        // Guard against a double-click firing a second delete for the same criterion
        // while the first request is still in flight.
        if (deletingIds.includes(criterionId)) return;
        setDeletingIds((ids) => [...ids, criterionId]);

        // Optimistically update local state
        const originalCriteria = [...criteria];
        setCriteria(criteria.filter((c) => c.id !== criterionId));

        // Call API to delete from backend
        try {
            await dispatch(deleteSelectionCriterion(criterionId)).unwrap();
            setApiError(null);
        } catch (error) {
            // Extract a readable message: Error instances stringify to "{}", so prefer
            // their .message; problem-detail objects (e.g. an Axios response body) keep
            // their JSON so Sentry shows the real detail instead of "[object Object]".
            const message =
                error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error);
            console.error('Failed to delete selection criterion:', message);
            // Revert local state on error
            setCriteria(originalCriteria);
            setApiError(deleteErrorText(error));
        } finally {
            setDeletingIds((ids) => ids.filter((id) => id !== criterionId));
        }
    };

    const handleCheckboxChange = async (criterionId: string, offerSubmissionId: string, isChecked: boolean) => {
        // Get current results for this criterion
        const criterion = criteria.find((c) => c.id === criterionId);
        if (!criterion) return;

        // Build the complete results array with all offer submissions
        const updatedResults: SelectionCriterionResult[] = offerSubmissions.map((offerSubmission) => {
            // Find existing result for this offer submission
            const existingResult = criterion.results.find((r) => r.offerSubmissionId === offerSubmission.id);

            if (offerSubmission.id === offerSubmissionId) {
                // This is the checkbox that was clicked
                return {
                    offerSubmissionId: offerSubmission.id,
                    fulfilled: isChecked,
                };
            } else if (existingResult) {
                // Preserve existing value
                return existingResult;
            } else {
                // Default to false for unchecked
                return {
                    offerSubmissionId: offerSubmission.id,
                    fulfilled: false,
                };
            }
        });

        // Save original state before optimistic update
        const originalCriteria = [...criteria];

        // Optimistically update local state
        setCriteria(criteria.map((c) => (c.id === criterionId ? { ...c, results: updatedResults } : c)));

        // Call API to persist changes
        try {
            await dispatch(updateSelectionCriterionResults({ criterionId, results: updatedResults })).unwrap();
            setApiError(null);
        } catch (error) {
            console.error('Failed to update selection criterion results:', error);
            // Revert to original state on error
            setCriteria(originalCriteria);
            setApiError(typeof error === 'string' ? error : 'Failed to update selection criterion results');
        }
    };

    const handleAddFromCatalog = async (catalogItem: SelectionCriteriaCatalogItem | null) => {
        if (!catalogItem) {
            setIsAddingFromCatalog(false);
            return;
        }

        // Check if criterion already exists
        if (criteria.some((c) => c.catalogItemId === catalogItem.id)) {
            setIsAddingFromCatalog(false);
            return;
        }

        setAddCriterionLoading(true);
        setApiError(null);

        try {
            // Prepare the request
            const request: AddSelectionCriterionRequest = {
                sourceCatalogId: catalogItem.id,
            };

            // Call the API - the Redux store will be updated automatically
            await dispatch(addSelectionCriterion(request)).unwrap();
            setIsAddingFromCatalog(false);
        } catch (error) {
            setApiError(typeof error === 'string' ? error : 'Failed to add selection criterion');
        } finally {
            setAddCriterionLoading(false);
        }
    };

    // Get available catalog items (not already added to tender)
    const availableCatalogItems = selectionCriteriaCatalog.filter(
        (catalogItem: SelectionCriteriaCatalogItem) => !criteria.some((c) => c.catalogItemId === catalogItem.id),
    );

    const handleCustomValueChange = (criterionId: string, value: string) => {
        // Update local state immediately
        setCriteria((prevCriteria) =>
            prevCriteria.map((criterion) => {
                if (criterion.id === criterionId) {
                    return { ...criterion, customValue: value, hasError: false };
                }
                return criterion;
            }),
        );
    };

    const handleFinalizeCustomValue = async (criterionId: string) => {
        try {
            const currentCriterion = criteria.find((c) => c.id === criterionId);
            if (!currentCriterion?.catalogItemId) {
                console.warn('Cannot update criterion without catalogItemId');
                return;
            }

            const value = currentCriterion.customValue || '';
            if (!value.trim()) return;

            const request: AddSelectionCriterionRequest = {
                sourceCatalogId: currentCriterion.catalogItemId,
                placeholderValue: value,
            };
            await dispatch(addSelectionCriterion(request)).unwrap();
            setApiError(null);
        } catch (error) {
            console.error('Failed to update criterion value:', error);
            setApiError(typeof error === 'string' ? error : 'Failed to update criterion value');
        }
    };

    // Helper to check if a title contains a placeholder (X)
    const hasPlaceholder = (title: string): boolean => {
        return /\bX\b/.test(title);
    };

    // Helper to render title with input for placeholders
    const renderCriteriaTitle = (criteriaItem: CriteriaWithValue) => {
        if (hasPlaceholder(criteriaItem.title)) {
            const parts = criteriaItem.title.split(/\bX\b/);
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {parts.map((part, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="span" variant="body2">
                                {part}
                            </Typography>
                            {i < parts.length - 1 && (
                                <TextField
                                    size="small"
                                    placeholder={t('value')}
                                    value={criteriaItem.customValue || ''}
                                    onChange={(e) => handleCustomValueChange(criteriaItem.id, e.target.value)}
                                    onBlur={() => handleFinalizeCustomValue(criteriaItem.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleFinalizeCustomValue(criteriaItem.id);
                                        }
                                    }}
                                    variant="outlined"
                                    error={criteriaItem.hasError}
                                    helperText={criteriaItem.hasError ? t('Please provide a value') : ''}
                                    sx={{
                                        width: '120px',
                                        '& .MuiOutlinedInput-root': {
                                            height: '32px',
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '6px 10px',
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            );
        }
        return <Typography variant="body2">{criteriaItem.title}</Typography>;
    };

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Selection Criteria')}
            </Typography>

            {(tenderSelectionCriteriaLoading || selectionCriteriaLoading) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {tenderSelectionCriteriaError && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {tenderSelectionCriteriaError}
                </Alert>
            )}

            {apiError && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {apiError}
                </Alert>
            )}

            {!tenderSelectionCriteriaLoading && !selectionCriteriaLoading && !tenderSelectionCriteriaError && (
                <>
                    <TableContainer component={Paper} sx={{ mt: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        '& .MuiTableCell-root': { color: 'primary.contrastText' },
                                    }}
                                >
                                    <TableCell sx={{ width: 50 }} />
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('Criterion')}</TableCell>
                                    {offerSubmissions.map((offerSubmission) => (
                                        <TableCell key={offerSubmission.id} align="center">
                                            {offerSubmission.bidderName}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {criteria.map((c) => (
                                    <TableRow
                                        key={c.id}
                                        sx={{
                                            backgroundColor: (theme) =>
                                                criteria.indexOf(c) % 2 === 0
                                                    ? theme.palette.action.hover
                                                    : 'transparent',
                                        }}
                                    >
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteCriteria(c.id)}
                                                color="error"
                                                disabled={deletingIds.includes(c.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{renderCriteriaTitle(c)}</TableCell>
                                        {offerSubmissions.map((offerSubmission) => (
                                            <TableCell key={offerSubmission.id} align="center">
                                                <Checkbox
                                                    checked={
                                                        c.results.find(
                                                            (r) => r.offerSubmissionId === offerSubmission.id,
                                                        )?.fulfilled || false
                                                    }
                                                    onChange={(e) =>
                                                        handleCheckboxChange(c.id, offerSubmission.id, e.target.checked)
                                                    }
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}

                                {isAddingFromCatalog && (
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => setIsAddingFromCatalog(false)}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell colSpan={offerSubmissions.length + 1}>
                                            <Autocomplete<SelectionCriteriaCatalogItem>
                                                options={availableCatalogItems}
                                                getOptionLabel={(option) => option.title}
                                                onChange={(_, value) => handleAddFromCatalog(value)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder={t('Select criterion from catalog...')}
                                                        size="small"
                                                        autoFocus
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body2">{option.title}</Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                {option.selectedByDefault
                                                                    ? t('Default Criterion')
                                                                    : t('Optional')}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                                fullWidth
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setIsAddingFromCatalog(true)}
                            disabled={availableCatalogItems.length === 0 || isAddingFromCatalog || addCriterionLoading}
                        >
                            {addCriterionLoading ? t('Adding...') : t('Add from Catalog')}
                        </Button>
                        {availableCatalogItems.length === 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                                {t('All catalog criteria have been added')}
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            <StepperActions />
        </>
    );
}
